import React from 'react';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import {useAuth0Data} from '../../../context/Auth0DataContext';
import {format, parseISO} from 'date-fns';

const {FiCalendar, FiClock, FiAlertCircle, FiPlus, FiExternalLink} = FiIcons;

const MeetingStatusWidget = () => {
  const {data} = useAuth0Data();
  const {meetings} = data;

  const scheduledMeetings = meetings.filter(meeting => meeting.status === 'scheduled');
  const needToSchedule = meetings.filter(meeting => meeting.status === 'need-to-schedule');

  if (meetings.length === 0) {
    return (
      <Link to="/data-management/meetings" className="block">
        <motion.div
          whileHover={{y: -2}}
          className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 hover:shadow-md transition-all duration-200 cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiCalendar} className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="ml-3 text-lg font-semibold text-gray-900">Meeting Status</h3>
            </div>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-blue-600" />
            </div>
          </div>
          <div className="text-center py-12 text-gray-500">
            <SafeIcon icon={FiPlus} className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No meetings found.</p>
            <p className="text-sm">Click to add meetings</p>
          </div>
        </motion.div>
      </Link>
    );
  }

  return (
    <Link to="/data-management/meetings" className="block">
      <motion.div
        whileHover={{y: -2}}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 hover:shadow-md transition-all duration-200 cursor-pointer group"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiCalendar} className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="ml-3 text-lg font-semibold text-gray-900">Meeting Status</h3>
          </div>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-blue-600" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <SafeIcon icon={FiCalendar} className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-600">{scheduledMeetings.length}</p>
            <p className="text-sm text-gray-600">Scheduled</p>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <SafeIcon icon={FiAlertCircle} className="w-8 h-8 text-red-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-red-600">{needToSchedule.length}</p>
            <p className="text-sm text-gray-600">Need to Schedule</p>
          </div>
        </div>

        <div className="space-y-4">
          {scheduledMeetings.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2 text-green-600" />
                Upcoming Meetings
              </h4>
              <div className="space-y-2">
                {scheduledMeetings.slice(0, 3).map((meeting, index) => (
                  <motion.div
                    key={meeting.id}
                    initial={{opacity: 0, x: -10}}
                    animate={{opacity: 1, x: 0}}
                    transition={{delay: index * 0.1}}
                    className="flex items-center justify-between p-3 bg-green-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{meeting.title}</p>
                      <p className="text-xs text-gray-500">{meeting.client}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-green-600">
                        {format(parseISO(meeting.date), 'MMM dd')}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {needToSchedule.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                <SafeIcon icon={FiClock} className="w-4 h-4 mr-2 text-red-600" />
                Need to Schedule
              </h4>
              <div className="space-y-2">
                {needToSchedule.slice(0, 3).map((meeting, index) => (
                  <motion.div
                    key={meeting.id}
                    initial={{opacity: 0, x: -10}}
                    animate={{opacity: 1, x: 0}}
                    transition={{delay: index * 0.1}}
                    className="flex items-center justify-between p-3 bg-red-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{meeting.title}</p>
                      <p className="text-xs text-gray-500">{meeting.client}</p>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Urgent
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </Link>
  );
};

export default MeetingStatusWidget;